import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import Select from 'react-select';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import AgencyPerformanceReportController from 'pages/Controller/AgencyPerformanceReportController';
import Loader from 'utils/Loader';
import './AgencyPerformanceReport.css';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip, Legend);

const areAllOptionsSelected = (selectedOptions = [], selectOptions = []) => {
  const realOptions = selectOptions.filter((option) => option.value !== '__ALL__');
  const selectedValues = new Set(selectedOptions.map((option) => option.value));

  return realOptions.length > 0 && realOptions.every((option) => selectedValues.has(option.value));
};

const growthColor = (value) => {
  const numericValue = Number(value);
  if (numericValue > 0) return '#15803D';
  if (numericValue < 0) return '#DC2626';
  return '#475569';
};

const excludedSelectionPlaceholder = (label, selectedOptions = [], selectOptions = []) => {
  const realOptions = selectOptions.filter((option) => option.value !== '__ALL__');
  if (!selectedOptions.length || !realOptions.length) return label;

  const selectedValues = new Set(selectedOptions.map((option) => option.value));
  const excludedNames = realOptions
    .filter((option) => !selectedValues.has(option.value))
    .map((option) => option.label);

  if (!excludedNames.length) return label;
  const excludedText = excludedNames.join(', ');
  return `${label}: Excluded: ${excludedText.length > 45 ? `${excludedText.slice(0, 45)}...` : excludedText}`;
};

function AgencyPerformanceReport() {
  const [area, setArea] = useState([]);
  const [month, setMonth] = useState([]);
  const [division, setDivision] = useState([]);
  const [dealerPage, setDealerPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const [currentQuarterPage, setCurrentQuarterPage] = useState(1);
  const [lastQuarterPage, setLastQuarterPage] = useState(1);
  const [pendingTable, setPendingTable] = useState(null);
  const [areaInitialized, setAreaInitialized] = useState(false);
  const [monthInitialized, setMonthInitialized] = useState(false);
  const [divisionInitialized, setDivisionInitialized] = useState(false);

  const {
    dashboardData,
    cloading,
    divisionOptions,
    loadingZones,
    loadingDivisions,
    zoneOptions
  } = AgencyPerformanceReportController({
    area,
    month,
    division,
    dealerPage,
    paymentPage,
    filtersReady: areaInitialized && monthInitialized && divisionInitialized,
    divisionReady: divisionInitialized
  });

  const lastUpdated = useMemo(() => {
    const now = new Date();
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(now);
  }, []);

  const areaSelectOptions = useMemo(
    () => [
      { label: 'Select All', value: '__ALL__', isSelectAll: true },
      ...zoneOptions
    ],
    [zoneOptions]
  );

  const allAreasSelected = useMemo(
    () => areAllOptionsSelected(area, areaSelectOptions),
    [area, areaSelectOptions]
  );

  useEffect(() => {
    if (areaInitialized) return;
    if (loadingZones) return;

    const realOptions = areaSelectOptions.filter((option) => option.value !== '__ALL__');
    setArea(realOptions);
    setAreaInitialized(true);
  }, [areaInitialized, areaSelectOptions, loadingZones]);

  const handleAreaChange = (selected, actionMeta) => {
    if (actionMeta?.action === 'clear') {
      setArea([]);
      return;
    }

    if (!selected) {
      setArea([]);
      return;
    }

    if (selected.some((o) => o.value === '__ALL__')) {
      const allSelected = areAllOptionsSelected(area, areaSelectOptions);
      setArea(allSelected ? [] : areaSelectOptions.filter((o) => o.value !== '__ALL__'));
      return;
    }

    setArea(selected);
  };

  const handleMonthChange = (selected, actionMeta) => {
    if (actionMeta?.action === 'clear') {
      setMonth([]);
      return;
    }

    if (!selected) {
      setMonth([]);
      return;
    }

    if (selected.some((o) => o.value === '__ALL__')) {
      const allSelected = areAllOptionsSelected(month, monthSelectOptions);
      setMonth(allSelected ? [] : monthSelectOptions.filter((o) => o.value !== '__ALL__'));
      return;
    }

    setMonth(selected);
  };

  const handleDivisionChange = (selected, actionMeta) => {
    if (actionMeta?.action === 'clear') {
      setDivision([]);
      return;
    }

    if (!selected) {
      setDivision([]);
      return;
    }

    if (selected.some((o) => o.value === '__ALL__')) {
      const allSelected = areAllOptionsSelected(division, divisionSelectOptions);
      setDivision(allSelected ? [] : divisionSelectOptions.filter((o) => o.value !== '__ALL__'));
      return;
    }

    setDivision(selected);
  };

  const CheckboxOption = (props) => {
    const { label, isSelectAll } = props.data;
    const { selectOption } = props;
    const allSelected = Boolean(props.selectProps?.allItemsSelected);
    const selectedValues = Array.isArray(props.selectProps?.value)
      ? props.selectProps.value.map((option) => option?.value)
      : [];
    const checked = isSelectAll ? allSelected : selectedValues.includes(props.data.value);

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      selectOption(props.data);
    };

    const count = props.data.count;

    return (
      <button
        type="button"
        onMouseDown={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          width: '100%',
          background: '#fff',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={checked} readOnly />
          <span style={{ color: '#0F172A' }}>{label}</span>
        </span>
        {count !== undefined ? (
          <span style={{ color: '#6B7280', fontSize: 12 }}>{count}</span>
        ) : null}
      </button>
    );
  };

  const NoMultiValue = () => null;

  const monthOptions = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const fiscalStartYear = now.getMonth() >= 3 ? currentYear : currentYear - 1;
    const months = ['APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC', 'JAN', 'FEB', 'MAR'];

    return months.map((monthLabel, index) => {
      const year = index < 9 ? fiscalStartYear : fiscalStartYear + 1;
      return {
        value: `${monthLabel}-${String(year).slice(-2)}`,
        label: `${String(index + 1).padStart(2, '0')}_${monthLabel}`
      };
    });
  }, []);

  const monthSelectOptions = useMemo(
    () => [
      { label: 'Select All', value: '__ALL__', isSelectAll: true },
      ...monthOptions
    ],
    [monthOptions]
  );

  const allMonthsSelected = useMemo(
    () => areAllOptionsSelected(month, monthSelectOptions),
    [month, monthSelectOptions]
  );

  const areaPlaceholder = useMemo(
    () => excludedSelectionPlaceholder('AREA', area, areaSelectOptions),
    [area, areaSelectOptions]
  );

  const monthPlaceholder = useMemo(
    () => excludedSelectionPlaceholder('MONTH', month, monthSelectOptions),
    [month, monthSelectOptions]
  );

  useEffect(() => {
    if (monthInitialized) return;
    if (monthSelectOptions.length > 1) {
      setMonth(monthSelectOptions.filter((option) => option.value !== '__ALL__'));
      setMonthInitialized(true);
    }
  }, [monthInitialized, monthSelectOptions]);

  const divisionSelectOptions = useMemo(() => {
    return [{ label: 'Select All', value: '__ALL__', isSelectAll: true }, ...divisionOptions];
  }, [divisionOptions]);

  const allDivisionsSelected = useMemo(
    () => areAllOptionsSelected(division, divisionSelectOptions),
    [division, divisionSelectOptions]
  );

  const divisionPlaceholder = useMemo(
    () => excludedSelectionPlaceholder('DIVISION', division, divisionSelectOptions),
    [division, divisionSelectOptions]
  );

  useEffect(() => {
    if (loadingDivisions) return;

    const realOptions = divisionSelectOptions.filter((option) => option.value !== '__ALL__');

    if (!divisionInitialized) {
      setDivision(realOptions);
      setDivisionInitialized(true);
      return;
    }

    const realValues = new Set(realOptions.map((option) => option.value));
    const hasStaleSelection = division.length > 0 && division.every((option) => !realValues.has(option.value));

    if (hasStaleSelection) {
      setDivision(realOptions);
    }
  }, [division, divisionInitialized, divisionOptions.length, divisionSelectOptions, loadingDivisions]);

  useEffect(() => {
    setPendingTable(null);
    setDealerPage(1);
    setPaymentPage(1);
    setCurrentQuarterPage(1);
    setLastQuarterPage(1);
  }, [area, month, division]);

  useEffect(() => {
    if (!cloading) {
      setPendingTable(null);
    }
  }, [cloading]);

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      minHeight: 38,
      height: 38,
      borderRadius: 8,
      borderColor: '#D1D5DB',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#9CA3AF'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      height: 38,
      padding: '0 8px',
      overflow: 'hidden',
      flexWrap: 'nowrap'
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 38
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6B7280',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 'calc(100% - 4px)'
    })
  };

  const formatNumber = (value, maximumFractionDigits = 2) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const number = Number(String(value).replace(/,/g, ''));
    if (!Number.isFinite(number)) {
      return value;
    }

    return number.toLocaleString('en-IN', {
      maximumFractionDigits
    });
  };

  const formatLakh = (value) => {
    const formattedValue = formatNumber(value);
    return formattedValue === '-' ? formattedValue : `${formattedValue} L`;
  };

  const toNumber = (value) => {
    const number = Number(String(value ?? 0).replace(/,/g, ''));
    return Number.isFinite(number) ? number : 0;
  };

  const formatPercent = (value, label) => label ?? (value !== null && value !== undefined ? `${value}%` : '-');
  const fiscalKey = (yearLabel) => `fy${String(yearLabel || '').replace('-', '_')}`;
  const divisionLabel = (row) => [row?.divisionCode, row?.division].filter(Boolean).join(' ');

  const apiLastUpdated = useMemo(() => {
    if (!dashboardData?.lastUpdated) {
      return lastUpdated;
    }

    if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(String(dashboardData.lastUpdated))) {
      return dashboardData.lastUpdated;
    }

    const parsedDate = new Date(String(dashboardData.lastUpdated).replace(' ', 'T'));
    if (Number.isNaN(parsedDate.getTime())) {
      return dashboardData.lastUpdated;
    }

    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(parsedDate);
  }, [dashboardData?.lastUpdated, lastUpdated]);

  const summaryCards = useMemo(() => {
    const styleMap = {
      saleMtr: { bg: '#ECF3FF', color: '#1E3A8A', icon: InsertChartOutlinedIcon },
      saleVal: { bg: '#E8F8F6', color: '#047857', icon: MonetizationOnOutlinedIcon },
      lyGrowthVol: { bg: '#F5F3FF', color: '#7C3AED', icon: TrendingUpOutlinedIcon },
      lyGrowthVal: { bg: '#FFF7ED', color: '#C2410C', icon: TrendingDownOutlinedIcon },
      netOutstanding: { bg: '#FEF2F2', color: '#B91C1C', icon: AccountBalanceWalletOutlinedIcon }
    };

    const cards = Array.isArray(dashboardData?.summary?.cards) ? dashboardData.summary.cards : [];
    return cards.map((card) => {
      const isLakhValue = ['saleMtr', 'saleVal', 'netOutstanding'].includes(card.key);
      const isSalesAchievement = ['saleMtr', 'saleVal'].includes(card.key);
      return {
      ...card,
      title: isLakhValue ? `${card.title} (L)` : card.title,
      value: card.valueLabel || (isLakhValue ? formatLakh(card.value) : formatNumber(card.value)),
      caption: isSalesAchievement ? (card.achievementPercentLabel || '') : (card.changeLabel || card.growthLabel || ''),
      captionColor: ['lyGrowthVol', 'lyGrowthVal'].includes(card.key)
        ? growthColor(card.value)
        : (styleMap[card.key] || styleMap.saleMtr).color,
      totalCaption: card.totalLabel !== undefined && card.totalLabel !== null && Number(card.totalLabel) !== 0 ? formatLakh(card.totalLabel) : '',
      ...(styleMap[card.key] || styleMap.saleMtr)
    };
    });
  }, [dashboardData]);

  const budgetRows = dashboardData?.budgetVsAchievement?.data || [];
  const budgetTotal = dashboardData?.budgetVsAchievement?.grandTotal;
  const growthVolume = dashboardData?.growthVolume || {};
  const growthValue = dashboardData?.growthValue || {};
  const currentQuarter = dashboardData?.quarterWiseBreakup?.currentYear || {};
  const lastQuarter = dashboardData?.quarterWiseBreakup?.lastYear || {};
  const paginateQuarter = (data = [], requestedPage = 1) => {
    const pageSize = 5;
    const totalPages = Math.ceil(data.length / pageSize);
    const page = Math.max(1, Math.min(requestedPage, totalPages || 1));
    return {
      rows: data.slice((page - 1) * pageSize, page * pageSize),
      pagination: { page, pageSize, totalPages, totalRecords: data.length }
    };
  };
  const currentQuarterView = paginateQuarter(currentQuarter.data, currentQuarterPage);
  const lastQuarterView = paginateQuarter(lastQuarter.data, lastQuarterPage);
  const dealerPerformance = dashboardData?.dealerPerformance || {};
  const paymentOutstanding = dashboardData?.paymentOutstanding || {};
  const paymentSummary = paymentOutstanding.summary || {};
  const dealerPagination = dealerPerformance.pagination || {};
  const paymentPagination = paymentOutstanding.pagination || {};
  const budgetGraphRows = useMemo(() => {
    return budgetRows.slice(0, 6).map((row) => {
      const saleMtr = toNumber(row.saleMtr);
      const budgetMtr = toNumber(row.budgetMtr);

      return {
        label: divisionLabel(row) || '-',
        value: saleMtr,
        total: budgetMtr
      };
    });
  }, [budgetRows]);
  const quarterGraphRows = useMemo(() => {
    const total = currentQuarter?.grandTotal || {};
    return [
      { label: 'Q1', value: toNumber(total.q1), sales: total.q1Sales, color: '#2563EB' },
      { label: 'Q2', value: toNumber(total.q2), sales: total.q2Sales, color: '#059669' },
      { label: 'Q3', value: toNumber(total.q3), sales: total.q3Sales, color: '#D97706' },
      { label: 'Q4', value: toNumber(total.q4), sales: total.q4Sales, color: '#7C3AED' }
    ];
  }, [currentQuarter]);
  const quarterGraphTotal = quarterGraphRows.reduce((sum, item) => sum + Math.max(0, item.value), 0);
  const lastQuarterGraphRows = useMemo(() => {
    const total = lastQuarter?.grandTotal || {};
    return [
      { label: 'Q1', value: toNumber(total.q1), sales: total.q1Sales, color: '#2563EB' },
      { label: 'Q2', value: toNumber(total.q2), sales: total.q2Sales, color: '#059669' },
      { label: 'Q3', value: toNumber(total.q3), sales: total.q3Sales, color: '#D97706' },
      { label: 'Q4', value: toNumber(total.q4), sales: total.q4Sales, color: '#7C3AED' }
    ];
  }, [lastQuarter]);
  const lastQuarterGraphTotal = lastQuarterGraphRows.reduce((sum, item) => sum + Math.max(0, item.value), 0);
  const paymentAgingRows = useMemo(() => {
    return [
      { label: 'M47', value: toNumber(paymentSummary.m47), color: '#D97706' },
      { label: 'M60', value: toNumber(paymentSummary.m60), color: '#DB2777' },
      { label: 'M90', value: toNumber(paymentSummary.m90), color: '#DC2626' },
      { label: 'M120', value: toNumber(paymentSummary.m120), color: '#9333EA' },
      { label: 'M180', value: toNumber(paymentSummary.m180), color: '#4F46E5' },
      { label: 'M365', value: toNumber(paymentSummary.m365), color: '#0891B2' }
    ];
  }, [paymentSummary]);
  const paymentAgingMax = Math.max(...paymentAgingRows.map((item) => Math.abs(item.value)), 0);
  const chartTooltipLabel = (context) => {
    const label = context.dataset?.label ? `${context.dataset.label}: ` : '';
    return `${label}${formatNumber(context.raw)}`;
  };
  const compactChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          color: '#334155',
          font: { size: 11, weight: '700' }
        }
      },
      tooltip: {
        callbacks: {
          label: chartTooltipLabel
        }
      }
    }
  };
  const budgetChartData = useMemo(() => ({
    labels: budgetGraphRows.map((item) => item.label),
    datasets: [
      {
        label: 'Sale MTR (L)',
        data: budgetGraphRows.map((item) => item.value),
        backgroundColor: 'rgba(37, 99, 235, 0.82)',
        borderRadius: 7,
        maxBarThickness: 24
      },
      {
        label: 'Budget MTR (L)',
        data: budgetGraphRows.map((item) => item.total),
        backgroundColor: 'rgba(148, 163, 184, 0.55)',
        borderRadius: 7,
        maxBarThickness: 24
      }
    ]
  }), [budgetGraphRows]);
  const budgetChartOptions = {
    ...compactChartOptions,
    plugins: {
      ...compactChartOptions.plugins,
      datalabels: {
        display: (context) => Number(context.dataset.data[context.dataIndex]) > 0,
        color: '#FFFFFF',
        anchor: 'center',
        align: 'center',
        rotation: -90,
        clamp: true,
        font: { size: 10, weight: '600' },
        formatter: (value) => formatNumber(value, 2)
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#475569', font: { size: 10, weight: '700' }, maxRotation: 35, minRotation: 0 }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(148, 163, 184, 0.22)' },
        title: { display: true, text: 'MTR (L)', color: '#64748B' },
        ticks: {
          color: '#64748B',
          font: { size: 10 },
          maxTicksLimit: 6,
          precision: 2,
          callback: (value) => formatNumber(value, 2)
        }
      }
    }
  };
  const growthTrendLabels = growthVolume.years || growthValue.years || [];
  const growthTrendData = useMemo(() => ({
    labels: growthTrendLabels,
    datasets: [
      {
        label: 'Volume (L)',
        data: growthTrendLabels.map((year) => toNumber(growthVolume.totalRow?.[fiscalKey(year)])),
        borderColor: '#2563EB',
        backgroundColor: 'rgba(37, 99, 235, 0.12)',
        pointBackgroundColor: '#2563EB',
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 4,
        tension: 0.35,
        fill: true
      },
      {
        label: 'Value (L)',
        data: growthTrendLabels.map((year) => toNumber(growthValue.totalRow?.[fiscalKey(year)])),
        borderColor: '#F97316',
        backgroundColor: 'rgba(249, 115, 22, 0.10)',
        pointBackgroundColor: '#F97316',
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 4,
        tension: 0.35,
        fill: true
      }
    ]
  }), [growthTrendLabels, growthValue.totalRow, growthVolume.totalRow]);
  const growthTrendOptions = {
    ...compactChartOptions,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      ...compactChartOptions.plugins,
      legend: {
        ...compactChartOptions.plugins.legend,
        align: 'end'
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#475569', font: { size: 11, weight: '800' } }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(148, 163, 184, 0.2)' },
        ticks: { color: '#64748B', font: { size: 10 }, callback: (value) => formatNumber(value, 0) }
      }
    }
  };
  const quarterChartData = useMemo(() => ({
    labels: quarterGraphRows.map((item) => item.label),
    datasets: [
      {
        data: quarterGraphRows.map((item) => item.value),
        backgroundColor: quarterGraphRows.map((item) => item.color),
        borderColor: '#FFFFFF',
        borderWidth: 3,
        hoverOffset: 8
      }
    ]
  }), [quarterGraphRows]);
  const lastQuarterChartData = useMemo(() => ({
    labels: lastQuarterGraphRows.map((item) => item.label),
    datasets: [
      {
        data: lastQuarterGraphRows.map((item) => item.value),
        backgroundColor: lastQuarterGraphRows.map((item) => item.color),
        borderColor: '#FFFFFF',
        borderWidth: 3,
        hoverOffset: 8
      }
    ]
  }), [lastQuarterGraphRows]);
  const quarterChartOptions = {
    ...compactChartOptions,
    cutout: '62%',
    plugins: {
      ...compactChartOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${formatPercent(context.raw)}`
        }
      }
    }
  };
  const paymentAgingChartData = useMemo(() => ({
    labels: paymentAgingRows.map((item) => item.label),
    datasets: [
      {
        label: 'Amount',
        data: paymentAgingRows.map((item) => Math.abs(item.value)),
        backgroundColor: paymentAgingRows.map((item) => item.color),
        borderRadius: 8,
        maxBarThickness: 18
      }
    ]
  }), [paymentAgingRows]);
  const paymentAgingChartOptions = {
    ...compactChartOptions,
    indexAxis: 'y',
    plugins: {
      ...compactChartOptions.plugins,
      legend: { display: false }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: 'rgba(148, 163, 184, 0.22)' },
        ticks: { color: '#64748B', font: { size: 10 }, callback: (value) => formatNumber(value, 0) }
      },
      y: {
        grid: { display: false },
        ticks: { color: '#475569', font: { size: 11, weight: '800' } }
      }
    }
  };
  const rowsPerPage = 10;
  const tableCellSx = {
    py: 1,
    whiteSpace: 'nowrap'
  };
  const sectionPaperSx = {
    p: { xs: 1.75, md: 2.25 },
    borderRadius: 2,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    boxShadow: '0 4px 18px rgba(15, 23, 42, 0.045)',
    overflow: 'hidden',
    '& .MuiTableHead-root .MuiTableCell-root': {
      color: '#0F172A',
      fontWeight: 800,
      borderBottom: '1px solid #CBD5E1'
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
      borderBottom: '1px solid #EEF2F7'
    }
  };
  const nameCellSx = {
    ...tableCellSx,
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };
  const dealerRows = (dealerPerformance.data || []).slice(0, rowsPerPage);
  const paymentRows = (paymentOutstanding.data || []).slice(0, rowsPerPage);
  const dealerTableLoading = cloading && pendingTable === 'dealer';
  const paymentTableLoading = cloading && pendingTable === 'payment';
  const firstLoadInProgress = !pendingTable && (!areaInitialized || !monthInitialized || !divisionInitialized || (cloading && !dashboardData));
  const dataRefreshInProgress = cloading && !pendingTable && !firstLoadInProgress;
  const firstLoadText = !areaInitialized || loadingZones
    ? 'Loading area...'
    : !monthInitialized
      ? 'Loading month...'
      : !divisionInitialized || loadingDivisions
        ? 'Loading division...'
        : 'Loading Agency data...';
  const loadingSteps = [];
  const paymentHeatFields = ['netAmt', 'gAmt', 'm47', 'm60', 'm90', 'm120', 'm180', 'm365'];
  const paymentHeatMax = useMemo(() => {
    const values = paymentRows.flatMap((row) => paymentHeatFields.map((field) => Math.abs(Number(row[field]) || 0)));
    return Math.max(...values, 0);
  }, [paymentRows]);
  const paymentHeatSx = (value) => {
    const numericValue = Math.abs(Number(value) || 0);
    if (!numericValue || !paymentHeatMax) {
      return { backgroundColor: '#DED2CF', color: '#374151' };
    }

    const intensity = Math.max(0.18, Math.min(0.92, numericValue / paymentHeatMax));
    return {
      backgroundColor: `rgba(220, 38, 38, ${intensity})`,
      color: intensity > 0.55 ? '#111827' : '#374151'
    };
  };
  const NoDataRow = ({ colSpan }) => (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 6, color: '#6B7280', fontWeight: 600 }}>
        No data found
      </TableCell>
    </TableRow>
  );
  const MiniGraphPanel = ({ title, children, accent = '#2563EB' }) => (
    <Paper
      sx={{
        p: 0,
        borderRadius: 2,
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        boxShadow: '0 18px 42px rgba(15, 23, 42, 0.11)',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.15,
          background: `linear-gradient(135deg, ${accent}18 0%, #FFFFFF 78%)`,
          borderTop: `3px solid ${accent}`,
          borderBottom: '1px solid #EEF2F7'
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', color: '#0F172A', fontWeight: 900, letterSpacing: 0.35 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 1.75 }}>
        {children}
      </Box>
    </Paper>
  );
  const EmptyGraph = () => (
    <Box sx={{ minHeight: 142, display: 'grid', placeItems: 'center', color: '#94A3B8', fontWeight: 700, fontSize: 13 }}>
      No graph data
    </Box>
  );
  const TableLoader = () => (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(1px)'
      }}
    >
      <Loader />
      <Typography variant="caption" sx={{ mt: 1, color: '#475569', fontWeight: 700 }}>
        Loading...
      </Typography>
    </Box>
  );
  const FirstLoadOverlay = () => (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 14000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: 'rgba(0, 0, 0, 0.50)',
        backdropFilter: 'blur(0.5px)'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Loader />
          <Typography sx={{ color: '#FFFFFF', fontWeight: 700 }}>
            {firstLoadText}
          </Typography>
        </Box>
        <Box sx={{ display: 'none' }}>
          {loadingSteps.map((step) => (
            <Box
              key={step.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.9,
                borderRadius: 1.5,
                backgroundColor: step.done ? '#F0FDF4' : step.active ? '#EFF6FF' : '#F8FAFC',
                border: `1px solid ${step.done ? '#BBF7D0' : step.active ? '#BFDBFE' : '#E2E8F0'}`
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  flex: '0 0 auto',
                  color: step.done ? '#15803D' : step.active ? '#1D4ED8' : '#94A3B8',
                  backgroundColor: '#FFFFFF',
                  fontSize: 13,
                  fontWeight: 900,
                  border: '1px solid currentColor'
                }}
              >
                {step.done ? '✓' : step.active ? '•' : ''}
              </Box>
              <Typography sx={{ color: step.done ? '#166534' : step.active ? '#1D4ED8' : '#64748B', fontWeight: 700 }}>
                {step.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
  const DataRefreshOverlay = () => (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 14000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: 'rgba(0, 0, 0, 0.50)',
        backdropFilter: 'blur(0.5px)'
      }}
    >
      <Loader />
      <Typography sx={{ mt: 1, color: '#FFFFFF', fontWeight: 700 }}>
        Data loading...
      </Typography>
    </Box>
  );
  const formatPaginationRange = (pagination = {}) => {
    const page = Number(pagination.page || 0);
    const pageSize = Number(pagination.pageSize || rowsPerPage);
    const totalRecords = Number(pagination.totalRecords || 0);
    if (!page || !pageSize || !totalRecords) {
      return '0 - 0 / 0';
    }

    const start = ((page - 1) * pageSize) + 1;
    const end = Math.min(page * pageSize, totalRecords);
    return `${formatNumber(start, 0)} - ${formatNumber(end, 0)} / ${formatNumber(totalRecords, 0)}`;
  };
  const PaginationFooter = ({ pagination, onPrev, onNext, loading = false }) => (
    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.25 }}>
      <Typography variant="body1" sx={{ color: '#374151', fontWeight: 500 }}>
        {formatPaginationRange(pagination)}
      </Typography>
      <IconButton
        size="medium"
        disabled={loading || !pagination.page || pagination.page <= 1}
        onClick={onPrev}
        sx={{ color: '#6B7280', p: 0.5, '&.Mui-disabled': { color: '#C7CDD4' } }}
      >
        <ChevronLeftIcon fontSize="medium" />
      </IconButton>
      <IconButton
        size="medium"
        disabled={loading || !pagination.totalPages || pagination.page >= pagination.totalPages}
        onClick={onNext}
        sx={{ color: '#111827', p: 0.5, '&.Mui-disabled': { color: '#C7CDD4' } }}
      >
        <ChevronRightIcon fontSize="medium" />
      </IconButton>
    </Box>
  );
  return (
    <>
    {firstLoadInProgress ? <FirstLoadOverlay /> : null}
    {dataRefreshInProgress ? <DataRefreshOverlay /> : null}
      <div className="agency-performance-report">
        <Box sx={{ px: { xs: 1.25, md: 2.5 }, pt: { xs: 1.5, md: 2.5 }, pb: 3 }}>
          <Paper sx={{ ...sectionPaperSx, mb: 2.5, borderTop: '4px solid #DC2626', background: 'linear-gradient(120deg, #FFFFFF 0%, #F1F5F9 100%)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: 0.4, color: '#0F172A' }}>
                Agency Performance
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', mt: 0.25, fontWeight: 600 }}>
                Sales, growth and collections overview
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ width: '100%', maxWidth: 440 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#475569', fontWeight: 700 }}>
                  AREA
                </Typography>
                <Select
                  isMulti
                  options={areaSelectOptions}
                  value={area}
                  onChange={handleAreaChange}
                  allItemsSelected={allAreasSelected}
                  getOptionValue={(opt) => opt.value}
                  getOptionLabel={(opt) => opt.label}
                  placeholder={areaPlaceholder}
                  styles={selectStyles}
                  components={{ Option: CheckboxOption, MultiValue: NoMultiValue }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  controlShouldRenderValue={false}
                  menuPortalTarget={document.body}
                  classNamePrefix="react-select"
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' }, gap: 1, width: '100%' }}>
              <Typography variant="body1" sx={{ px: 1.75, py: 0.85, borderRadius: 1.5, backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)', color: '#0F172A', fontWeight: 600, fontSize: { xs: 14, md: 16 } }}>
                Updated as on : {apiLastUpdated}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={1.5} sx={{ mb: 2, alignItems: 'stretch' }}>
        {summaryCards.map((card) => {
          const CardIcon = card.icon;
          return (
            <Grid item xs={12} sm={6} md={2.4} key={card.title}>
              <Paper
                sx={{
                  p: 1.75,
                  borderRadius: 2,
                  backgroundColor: '#FFFFFF',
                  border: `1px solid ${card.color}33`,
                  borderTop: `3px solid ${card.color}`,
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.075)',
                  minHeight: 116,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.1)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: card.color, fontWeight: 700, maxWidth: 'calc(100% - 40px)' }}>
                    {card.title}
                  </Typography>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1.5, backgroundColor: `${card.color}14`, display: 'grid', placeItems: 'center' }}>
                    <CardIcon sx={{ color: card.color, fontSize: 18 }} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.75, color: '#0F172A', lineHeight: 1.1 }}>
                    {card.value}
                  </Typography>
                  {card.caption ? (
                    <Typography variant="caption" sx={{ color: card.captionColor, display: 'block', mt: 0.5, fontWeight: 700 }}>
                      {card.caption}
                    </Typography>
                  ) : (
                    <Box sx={{ height: 16 }} />
                  )}
                  {card.totalCaption ? (
                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.5, textAlign: 'right' }}>
                      {card.totalCaption}
                    </Typography>
                  ) : null}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ ...sectionPaperSx, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 34, height: 34, borderRadius: 1.5, backgroundColor: '#EEF2FF', display: 'grid', placeItems: 'center' }}>
                <InsertChartOutlinedIcon sx={{ color: '#4338CA', fontSize: 18 }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: 0.2 }}>
                SALES ANALYTICS
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#475569', fontWeight: 700 }}>
              MONTH
            </Typography>
            <Select
              isMulti
              options={monthSelectOptions}
              value={month}
              onChange={handleMonthChange}
              allItemsSelected={allMonthsSelected}
              getOptionValue={(opt) => opt.value}
              getOptionLabel={(opt) => opt.label}
              placeholder={monthPlaceholder}
              styles={selectStyles}
              components={{ Option: CheckboxOption, MultiValue: NoMultiValue }}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              menuPortalTarget={document.body}
              classNamePrefix="react-select"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#475569', fontWeight: 700 }}>
              DIVISION
            </Typography>
            <Select
              isMulti
              options={divisionSelectOptions}
              value={division}
              onChange={handleDivisionChange}
              allItemsSelected={allDivisionsSelected}
              getOptionValue={(opt) => opt.value}
              getOptionLabel={(opt) => opt.label}
              placeholder={divisionPlaceholder}
              styles={selectStyles}
              components={{ Option: CheckboxOption, MultiValue: NoMultiValue }}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              menuPortalTarget={document.body}
              classNamePrefix="react-select"
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#0F172A', letterSpacing: 0.2 }}>
          1. BUDGET VS ACHIEVEMENT
        </Typography>
        <Grid container spacing={1.5} alignItems="stretch" sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E6EBF5', borderRadius: 1.5, height: '100%' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#EEF2FF' }}>
                    <TableCell sx={{ fontWeight: 700 }}>DIVISION</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>BUDGET MTR (L)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{dashboardData?.budgetVsAchievement?.currentYearLabel || ''} SALE MTR (L)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>BUDGET VAL (L)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{dashboardData?.budgetVsAchievement?.currentYearLabel || ''} SALE VAL (L)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>BDGT % (VOL)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {budgetRows.map((row) => (
                    <TableRow key={row.divisionCode || row.division} hover>
                      <TableCell>{divisionLabel(row)}</TableCell>
                      <TableCell align="right">{formatLakh(row.budgetMtr)}</TableCell>
                      <TableCell align="right">{formatLakh(row.saleMtr)}</TableCell>
                      <TableCell align="right">{formatLakh(row.budgetVal)}</TableCell>
                      <TableCell align="right">{formatLakh(row.saleVal)}</TableCell>
                      <TableCell align="right" sx={{ color: '#DC2626' }}>{formatPercent(row.gapPercent, row.gapPercentLabel)}</TableCell>
                    </TableRow>
                  ))}
                  {budgetTotal ? (
                    <TableRow sx={{ backgroundColor: '#F8FBFF', fontWeight: 700 }}>
                      <TableCell sx={{ fontWeight: 700 }}>{budgetTotal.division}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatLakh(budgetTotal.budgetMtr)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatLakh(budgetTotal.saleMtr)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatLakh(budgetTotal.budgetVal)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatLakh(budgetTotal.saleVal)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#DC2626' }}>{formatPercent(budgetTotal.gapPercent, budgetTotal.gapPercentLabel)}</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} md={4}>
            <MiniGraphPanel title="BUDGET ACHIEVEMENT SNAPSHOT" accent="#2563EB">
              {budgetGraphRows.length ? (
                <Box sx={{ height: 260 }}>
                  <Bar data={budgetChartData} options={budgetChartOptions} />
                </Box>
              ) : (
                <EmptyGraph />
              )}
            </MiniGraphPanel>
          </Grid>
        </Grid>

        <Paper
          sx={{
            p: 0,
            mb: 2,
            borderRadius: 2,
            border: '1px solid #DCE7FF',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 18px 44px rgba(15, 23, 42, 0.1)',
            overflow: 'hidden'
          }}
        >
          <Grid container>
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  height: '100%',
                  minHeight: 250,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  background: 'linear-gradient(160deg, #EFF6FF 0%, #FFFFFF 58%, #FFF7ED 100%)',
                  borderRight: { md: '1px solid #DCE7FF' }
                }}
              >
                <Typography variant="caption" sx={{ color: '#2563EB', fontWeight: 900, letterSpacing: 0.4 }}>
                  YEARLY PERFORMANCE TREND
                </Typography>
                <Typography sx={{ mt: 0.75, color: '#0F172A', fontWeight: 900, fontSize: { xs: 18, md: 22 }, lineHeight: 1.15 }}>
                  Volume and value movement
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75, color: '#64748B', fontWeight: 600 }}>
                  Fiscal-year totals for sales volume and value.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ px: 1, py: 0.7, borderRadius: 999, backgroundColor: '#DBEAFE', color: '#1D4ED8', fontSize: 12, fontWeight: 900 }}>
                    Volume
                  </Box>
                  <Box sx={{ px: 1, py: 0.7, borderRadius: 999, backgroundColor: '#FFEDD5', color: '#C2410C', fontSize: 12, fontWeight: 900 }}>
                    Value
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              {growthTrendLabels.length ? (
                <Box sx={{ height: 280, p: 2 }}>
                  <Line data={growthTrendData} options={growthTrendOptions} />
                </Box>
              ) : (
                <EmptyGraph />
              )}
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#FFFFFF', border: '1px solid #D1FAE5', boxShadow: '0 8px 20px rgba(15, 23, 42, 0.05)' }}>
              <Typography variant="subtitle2" sx={{ color: '#047857', fontWeight: 800, mb: 1, letterSpacing: 0.2 }}>
                2. GROWTH OVER LAST YEAR (VOLUME)
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700 }}>LY Growth % (Vol)</Typography>
                <Typography sx={{ color: growthColor(growthVolume.totalGrowth), fontWeight: 800 }}>{formatPercent(growthVolume.totalGrowth)}</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#D1FAE5' }}>
                    <TableCell>DIVISION</TableCell>
                    {(growthVolume.years || []).map((year) => (
                      <TableCell key={year} align="right">{year} SALE MTR (L)</TableCell>
                    ))}
                    <TableCell align="right">LY Growth % (Vol)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(growthVolume.data || []).map((row) => (
                    <TableRow key={row.divisionCode || row.division} hover>
                      <TableCell>{divisionLabel(row)}</TableCell>
                      {(growthVolume.years || []).map((year) => (
                        <TableCell key={year} align="right">{formatLakh(row[fiscalKey(year)])}</TableCell>
                      ))}
                      <TableCell align="right" sx={{ color: growthColor(row.growth), fontWeight: 700 }}>{formatPercent(row.growth, row.growthLabel)}</TableCell>
                    </TableRow>
                  ))}
                  {growthVolume.totalRow ? (
                    <TableRow sx={{ backgroundColor: '#EFFCF0', fontWeight: 700 }}>
                      <TableCell sx={{ fontWeight: 700 }}>{growthVolume.totalRow.division}</TableCell>
                      {(growthVolume.years || []).map((year) => (
                        <TableCell key={year} align="right" sx={{ fontWeight: 700 }}>{formatLakh(growthVolume.totalRow[fiscalKey(year)])}</TableCell>
                      ))}
                      <TableCell align="right" sx={{ color: growthColor(growthVolume.totalRow.growth), fontWeight: 700 }}>{formatPercent(growthVolume.totalRow.growth, growthVolume.totalRow.growthLabel)}</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#FFFFFF', border: '1px solid #FEF3C7', boxShadow: '0 8px 20px rgba(15, 23, 42, 0.05)' }}>
              <Typography variant="subtitle2" sx={{ color: '#B45309', fontWeight: 800, mb: 1, letterSpacing: 0.2 }}>
                3. GROWTH OVER LAST YEAR (VALUE)
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700 }}>LY Growth % (Val)</Typography>
                <Typography sx={{ color: growthColor(growthValue.totalGrowth), fontWeight: 800 }}>{formatPercent(growthValue.totalGrowth)}</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FEF3C7' }}>
                    <TableCell>DIVISION</TableCell>
                    {(growthValue.years || []).map((year) => (
                      <TableCell key={year} align="right">{year} SALE VAL (L)</TableCell>
                    ))}
                    <TableCell align="right">LY Growth % (Val)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(growthValue.data || []).map((row) => (
                    <TableRow key={row.divisionCode || row.division} hover>
                      <TableCell>{divisionLabel(row)}</TableCell>
                      {(growthValue.years || []).map((year) => (
                        <TableCell key={year} align="right">{formatLakh(row[fiscalKey(year)])}</TableCell>
                      ))}
                      <TableCell align="right" sx={{ color: growthColor(row.growth), fontWeight: 700 }}>{formatPercent(row.growth, row.growthLabel)}</TableCell>
                    </TableRow>
                  ))}
                  {growthValue.totalRow ? (
                    <TableRow sx={{ backgroundColor: '#FFFBEB', fontWeight: 700 }}>
                      <TableCell sx={{ fontWeight: 700 }}>{growthValue.totalRow.division}</TableCell>
                      {(growthValue.years || []).map((year) => (
                        <TableCell key={year} align="right" sx={{ fontWeight: 700 }}>{formatLakh(growthValue.totalRow[fiscalKey(year)])}</TableCell>
                      ))}
                      <TableCell align="right" sx={{ color: growthColor(growthValue.totalRow.growth), fontWeight: 700 }}>{formatPercent(growthValue.totalRow.growth, growthValue.totalRow.growthLabel)}</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ ...sectionPaperSx, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#4338CA', fontWeight: 800, mb: 2, letterSpacing: 0.2 }}>
            4. QUARTER WISE BREAKUP
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#FFFFFF', border: '1px solid #E6EBF5' }}>
                <Typography variant="subtitle2" sx={{ color: '#4338CA', fontWeight: 700, mb: 2 }}>
                  {currentQuarter.year || ''}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#EEF2FF' }}>
                      <TableCell>DIVISION</TableCell>
                      <TableCell align="center">Q1 (%)</TableCell>
                      <TableCell align="center">Q2 (%)</TableCell>
                      <TableCell align="center">Q3 (%)</TableCell>
                      <TableCell align="center">Q4 (%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentQuarterView.rows.map((row) => (
                      <TableRow key={row.divisionCode || row.division} hover>
                        <TableCell>{divisionLabel(row)}</TableCell>
                        <TableCell align="center">{formatPercent(row.q1, row.q1Label)}</TableCell>
                        <TableCell align="center">{formatPercent(row.q2, row.q2Label)}</TableCell>
                        <TableCell align="center">{formatPercent(row.q3, row.q3Label)}</TableCell>
                        <TableCell align="center">{formatPercent(row.q4, row.q4Label)}</TableCell>
                      </TableRow>
                    ))}
                    {currentQuarter.grandTotal ? (
                      <TableRow sx={{ backgroundColor: '#F8FBFF', fontWeight: 700 }}>
                        <TableCell sx={{ fontWeight: 700 }}>{currentQuarter.grandTotal.division}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{formatPercent(currentQuarter.grandTotal.q1, currentQuarter.grandTotal.q1Label)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{formatPercent(currentQuarter.grandTotal.q2, currentQuarter.grandTotal.q2Label)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{formatPercent(currentQuarter.grandTotal.q3, currentQuarter.grandTotal.q3Label)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{formatPercent(currentQuarter.grandTotal.q4, currentQuarter.grandTotal.q4Label)}</TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
                <PaginationFooter
                  pagination={currentQuarterView.pagination}
                  onPrev={() => setCurrentQuarterPage(currentQuarterView.pagination.page - 1)}
                  onNext={() => setCurrentQuarterPage(currentQuarterView.pagination.page + 1)}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#FFFFFF', border: '1px solid #E6EBF5' }}>
                <Typography variant="subtitle2" sx={{ color: '#4338CA', fontWeight: 700, mb: 2 }}>
                  {lastQuarter.year || ''}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#EEF2FF' }}>
                      <TableCell>DIVISION</TableCell>
                      <TableCell align="center">LY Q1 (%)</TableCell>
                      <TableCell align="center">LY Q2 (%)</TableCell>
                      <TableCell align="center">LY Q3 (%)</TableCell>
                      <TableCell align="center">LY Q4 (%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lastQuarterView.rows.map((row) => (
                      <TableRow key={row.divisionCode || row.division} hover>
                        <TableCell>{divisionLabel(row)}</TableCell>
                        <TableCell align="center">{formatPercent(row.q1, row.q1Label)}</TableCell>
                        <TableCell align="center">{formatPercent(row.q2, row.q2Label)}</TableCell>
                        <TableCell align="center">{formatPercent(row.q3, row.q3Label)}</TableCell>
                        <TableCell align="center">{formatPercent(row.q4, row.q4Label)}</TableCell>
                      </TableRow>
                    ))}
                    {lastQuarter.grandTotal ? (
                      <TableRow sx={{ backgroundColor: '#F8FBFF', fontWeight: 700 }}>
                        <TableCell sx={{ fontWeight: 700 }}>{lastQuarter.grandTotal.division}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{formatPercent(lastQuarter.grandTotal.q1, lastQuarter.grandTotal.q1Label)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{formatPercent(lastQuarter.grandTotal.q2, lastQuarter.grandTotal.q2Label)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{formatPercent(lastQuarter.grandTotal.q3, lastQuarter.grandTotal.q3Label)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{formatPercent(lastQuarter.grandTotal.q4, lastQuarter.grandTotal.q4Label)}</TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
                <PaginationFooter
                  pagination={lastQuarterView.pagination}
                  onPrev={() => setLastQuarterPage(lastQuarterView.pagination.page - 1)}
                  onNext={() => setLastQuarterPage(lastQuarterView.pagination.page + 1)}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <MiniGraphPanel title="QUARTER MIX" accent="#7C3AED">
                {quarterGraphTotal > 0 || lastQuarterGraphTotal > 0 ? (
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={6} lg={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#4338CA', fontWeight: 900, textAlign: 'center' }}>
                        {currentQuarter.year || 'Current FY'}
                      </Typography>
                      {quarterGraphTotal > 0 ? (
                        <Box sx={{ height: 185, position: 'relative' }}>
                          <Doughnut data={quarterChartData} options={quarterChartOptions} />
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: '58px 0 auto 0',
                              display: { xs: 'none', sm: 'grid' },
                              placeItems: 'center',
                              pointerEvents: 'none'
                            }}
                          >
                            <Typography sx={{ color: '#0F172A', fontWeight: 900, fontSize: 12 }}>
                              {currentQuarter.year || 'Current FY'}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <EmptyGraph />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} lg={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#64748B', fontWeight: 900, textAlign: 'center' }}>
                        {lastQuarter.year || 'Previous FY'}
                      </Typography>
                      {lastQuarterGraphTotal > 0 ? (
                        <Box sx={{ height: 185, position: 'relative' }}>
                          <Doughnut data={lastQuarterChartData} options={quarterChartOptions} />
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: '58px 0 auto 0',
                              display: { xs: 'none', sm: 'grid' },
                              placeItems: 'center',
                              pointerEvents: 'none'
                            }}
                          >
                            <Typography sx={{ color: '#0F172A', fontWeight: 900, fontSize: 12 }}>
                              {lastQuarter.year || 'Previous FY'}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <EmptyGraph />
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <EmptyGraph />
                )}
              </MiniGraphPanel>
            </Grid>
          </Grid>
        </Paper>
      </Paper>

      <Grid container spacing={1.5} sx={{ mb: 0 }}>
        <Grid item xs={12}>
          <Paper sx={{ ...sectionPaperSx }}>
            <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Grid item>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: 0.2 }}>
                  5. DEALER PERFORMANCE (VALUE)
                </Typography>
              </Grid>
            </Grid>
            <TableContainer sx={{ position: 'relative', minHeight: 220 }}>
              {dealerTableLoading ? <TableLoader /> : null}
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FFAB91' }}>
                    <TableCell>#</TableCell>
                    <TableCell>Sold-to-Party Name</TableCell>
                    <TableCell>CITY</TableCell>
                    {(dealerPerformance.years || []).map((year) => (
                      <TableCell key={year} align="right">{year} VAL (L)</TableCell>
                    ))}
                    <TableCell align="right">YTD Growth %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dealerRows.length ? (
                    dealerRows.map((row) => (
                      <TableRow key={row.dealerCode || row.dealerName} hover>
                        <TableCell>{row.rank}</TableCell>
                        <TableCell>{row.dealerName}</TableCell>
                        <TableCell>{row.city}</TableCell>
                        {(dealerPerformance.years || []).map((year) => (
                          <TableCell key={year} align="right">{formatLakh(row[fiscalKey(year)])}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ color: growthColor(row.lyGrowth), fontWeight: 700 }}>
                          {formatPercent(row.lyGrowth, row.lyGrowthLabel)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <NoDataRow colSpan={4 + (dealerPerformance.years || []).length} />
                  )}
                  {dealerRows.length && dealerPerformance.totalRow ? (
                    <TableRow sx={{ backgroundColor: '#E0E7FF' }}>
                      <TableCell colSpan={3} align="right" sx={{ fontWeight: 800 }}>
                        Grand Total
                      </TableCell>
                      {(dealerPerformance.years || []).map((year) => (
                        <TableCell key={year} align="right" sx={{ fontWeight: 800 }}>
                          {formatLakh(dealerPerformance.totalRow[fiscalKey(year)])}
                        </TableCell>
                      ))}
                      <TableCell
                        align="right"
                        sx={{ color: growthColor(dealerPerformance.totalRow.lyGrowth), fontWeight: 800 }}
                      >
                        {formatPercent(dealerPerformance.totalRow.lyGrowth, dealerPerformance.totalRow.lyGrowthLabel)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <PaginationFooter
              pagination={dealerPagination}
              loading={dealerTableLoading}
              onPrev={() => {
                setPendingTable('dealer');
                setDealerPage((page) => Math.max(1, page - 1));
              }}
              onNext={() => {
                setPendingTable('dealer');
                setDealerPage((page) => page + 1);
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ ...sectionPaperSx }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: '#0F172A', letterSpacing: 0.2 }}>
              6. PAYMENT & O/S
            </Typography>
            <Grid container spacing={1.25} alignItems="stretch" sx={{ mb: 2 }}>
              <Grid item xs={12} lg={8}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))', xl: 'repeat(7, minmax(0, 1fr))' }, gap: 1, height: '100%' }}>
                  {[
                    { label: 'G AMT', value: paymentSummary.gAmt, bg: '#F0FDF4', color: '#166534' },
                    { label: 'NET AMT', value: paymentSummary.netAmt, bg: '#EFF6FF', color: '#1D4ED8' },
                    { label: 'M47', value: paymentSummary.m47, bg: '#FEF3C7', color: '#92400E' },
                    { label: 'M60', value: paymentSummary.m60, bg: '#FCE7F3', color: '#BE185D' },
                    { label: 'M90', value: paymentSummary.m90, bg: '#FEE2E2', color: '#991B1B' },
                    { label: 'M180', value: paymentSummary.m180, bg: '#EEF2FF', color: '#3730A3' },
                    { label: 'M365', value: paymentSummary.m365, bg: '#ECFEFF', color: '#155E75' }
                  ].map((metric) => (
                    <Box
                      key={metric.label}
                      sx={{
                        px: 1,
                        py: 0.75,
                        borderRadius: 1.5,
                        backgroundColor: metric.bg,
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.75)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 58
                      }}
                    >
                      <Typography variant="caption" sx={{ color: metric.color, fontWeight: 600, textAlign: 'center' }}>
                        {metric.label}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.25, color: '#0F172A' }}>
                        {formatLakh(metric.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} lg={4}>
                <MiniGraphPanel title="PAYMENT AGING VIEW" accent="#DC2626">
                  {paymentAgingMax > 0 ? (
                    <Box sx={{ height: 230 }}>
                      <Bar data={paymentAgingChartData} options={paymentAgingChartOptions} />
                    </Box>
                  ) : (
                    <EmptyGraph />
                  )}
                </MiniGraphPanel>
              </Grid>
            </Grid>
            <TableContainer component={Paper} sx={{ position: 'relative', minHeight: 260, boxShadow: 'none', border: '1px solid #E6EBF5', borderRadius: 1.5 }}>
              {paymentTableLoading ? <TableLoader /> : null}
              <Table className="agency-payment-table" size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#EEF2FF' }}>
                    <TableCell>#</TableCell>
                    <TableCell>AREA</TableCell>
                    <TableCell>SOLD-TO PARTY</TableCell>
                    <TableCell>SOLD-TO PARTY NAME</TableCell>
                    <TableCell align="right">{paymentOutstanding.salesYearLabel || 'SALES VAL'} (L)</TableCell>
                    <TableCell align="center">NET O/S VAL (L)</TableCell>
                    <TableCell align="center">G AMT (L)</TableCell>
                    <TableCell align="right">M47 (L)</TableCell>
                    <TableCell align="right">M60 (L)</TableCell>
                    <TableCell align="right">M90 (L)</TableCell>
                    <TableCell align="right">M180 (L)</TableCell>
                    <TableCell align="right">M365 (L)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentRows.length ? (
                    paymentRows.map((row, index) => (
                      <TableRow key={row.partyCode || index} hover sx={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC' }}>
                        <TableCell>{((paymentPagination.page || 1) - 1) * (paymentPagination.pageSize || rowsPerPage) + index + 1}</TableCell>
                        <TableCell>{row.area}</TableCell>
                        <TableCell>{row.partyCode}</TableCell>
                        <TableCell>{row.partyName}</TableCell>
                        <TableCell align="right" sx={{ color: '#0F172A', fontWeight: 700 }}>{formatLakh(row.salesVal)}</TableCell>
                        <TableCell align="center" sx={{ ...paymentHeatSx(row.netAmt), fontWeight: 700 }}>{formatLakh(row.netAmt)}</TableCell>
                        <TableCell align="center" sx={paymentHeatSx(row.gAmt)}>{formatLakh(row.gAmt)}</TableCell>
                        <TableCell align="right" sx={paymentHeatSx(row.m47)}>{formatLakh(row.m47)}</TableCell>
                        <TableCell align="right" sx={paymentHeatSx(row.m60)}>{formatLakh(row.m60)}</TableCell>
                        <TableCell align="right" sx={paymentHeatSx(row.m90)}>{formatLakh(row.m90)}</TableCell>
                        <TableCell align="right" sx={paymentHeatSx(row.m180)}>{formatLakh(row.m180)}</TableCell>
                        <TableCell align="right" sx={paymentHeatSx(row.m365)}>{formatLakh(row.m365)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <NoDataRow colSpan={12} />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <PaginationFooter
              pagination={paymentPagination}
              loading={paymentTableLoading}
              onPrev={() => {
                setPendingTable('payment');
                setPaymentPage((page) => Math.max(1, page - 1));
              }}
              onNext={() => {
                setPendingTable('payment');
                setPaymentPage((page) => page + 1);
              }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  </div>
</>
  );
}

export default AgencyPerformanceReport;
